import React, { useEffect, useState } from "react";
import {
  Box, Button, IconButton, Paper,
  Stack, TextField, Typography
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import useApi from "hooks/useApi";

interface Props {
  transactionUid: string;
  transactionKey: string;
  teamUid: string;
  childData: boolean;
  onSuccess: () => void;
}

const InlineChildTransactionForm: React.FC<Props> = ({
  transactionUid,
  transactionKey,
  teamUid,
  childData,
  onSuccess,
}) => {
  const { createChildTransactions, showToast } = useApi();
  const [keys, setKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (transactionKey) {
      setKeys([`${transactionKey}-1`, `${transactionKey}-2`]);
    }
  }, [transactionKey]);

  const handleChange = (index: number, value: string) => {
    const updated = [...keys];
    updated[index] = value;
    setKeys(updated);
  };

  const addRow = () => {
    setKeys((prev) => [...prev, `${transactionKey}-${prev.length + 1}`]);
  };

  const removeRow = (index: number) => {
    const updated = keys.filter((_, i) => i !== index);
    setKeys(updated.length ? updated : [""]);
  };

  const handleClear = () => {
    setKeys([`${transactionKey}-1`, `${transactionKey}-2`]);
  };

  const handleSubmit = async () => {
    const cleaned = keys.map((k) => k.trim()).filter(Boolean);
    if (!cleaned.length) return;
    try {
      setLoading(true);
      const res = await createChildTransactions(transactionUid, {
        enabled: true,
        teamUid,
        childrenKeys: cleaned,
        childData,
      });
      if (res?.success) {
        showToast("Child transactions created successfully", "success");
        onSuccess();
      } else {
        showToast(res?.message || "Failed to create child transactions", "error");
      }
    } catch (err) {
      showToast("Something went wrong while creating child transactions", "error");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        background: "white",
        borderRadius: "10px",
        border: "1px solid",
        borderColor: "grey.200",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          border: "none",
          outline: "none",
          color: "#fff",
          px: 1,
          py: 1.5,
          backgroundColor: "#132D76",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          fontWeight={600}
          variant="h6"
          sx={{
            color: "#fff",
            fontSize: "14px",
            letterSpacing: "0.025em",
          }}
        >
          Create Sub Transactions
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ p: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Please enter sub transaction keys
        </Typography>

        <Stack spacing={1.2} mt={1.5}>
          {keys.map((key, index) => (
            <Box key={index} display="flex" gap={1} alignItems="center">
              <TextField
                fullWidth
                size="small"
                value={key}
                placeholder={`Key ${index + 1}`}
                onChange={(e) => handleChange(index, e.target.value)}
                InputProps={{ sx: { height: 34, fontSize: 13 } }}
              />
              {keys.length > 1 && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeRow(index)}
                  sx={{ p: 0.6 }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Stack>

        <Button
          startIcon={<Add />}
          size="small"
          sx={{ mt: 1, textTransform: "none" }}
          onClick={addRow}
        >
          Add row
        </Button>

        <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
          <Button size="small" onClick={handleClear}>
            Clear
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={loading}
            onClick={handleSubmit}
            sx={{
              minWidth: 90,
              backgroundColor: "#132D76",
              "&:hover": { backgroundColor: "#0f245c" },
            }}
          >
            {loading ? "Creating..." : "Create"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default InlineChildTransactionForm;